import {
  IWordCard,
  IWordCardMetadata,
  IWordCardWord,
  IWordCardWordsByLevel,
} from "@model";

export const WORD_CARD_LANGUAGES = [
  { key: "en", label: "English" },
  { key: "es", label: "Spanish (Español)" },
  { key: "fr", label: "French" },
  { key: "de", label: "German" },
  { key: "it", label: "Italian" },
  { key: "tr", label: "Turkish" },
  { key: "fa", label: "Persian (فارسی)" },
  { key: "ja", label: "Japanese (日本語)" },
  { key: "ko", label: "Korean (한국어)" },
  { key: "zh", label: "Chinese (中文)" },
] as const;

export const WORD_CARD_LEVELS = ["A1", "B1", "C1"] as const;

export type WordCardLanguageKey = (typeof WORD_CARD_LANGUAGES)[number]["key"];

export function wordCardListTitle(card: Partial<IWordCard>): string {
  const title = card.title?.trim();
  if (title) return title;
  const en = card.metadata?.en?.title?.trim();
  if (en) return en;
  return "Untitled";
}

export function cleanWordCardMetadata(
  metadata?: IWordCardMetadata | null,
): IWordCardMetadata {
  if (!metadata || typeof metadata !== "object") return {};
  return Object.fromEntries(
    Object.entries(metadata)
      .filter(([, value]) => value?.title?.trim() || value?.description?.trim())
      .map(([key, value]) => [
        key,
        {
          ...(value?.title?.trim() ? { title: value.title.trim() } : {}),
          ...(value?.description?.trim()
            ? { description: value.description.trim() }
            : {}),
        },
      ]),
  );
}

function cleanEntry(entry?: IWordCardWord[WordCardLanguageKey]) {
  if (!entry) return undefined;
  const word = entry.word?.trim();
  const partOfSpeech = entry.partOfSpeech?.trim();
  const more = (entry.more ?? []).map((line) => line.trim()).filter(Boolean);
  const samples = (entry.samples ?? [])
    .map((sample) => {
      const text = sample?.text?.trim();
      if (!text) return null;
      const highlight = sample?.highlight?.trim();
      return {
        text,
        ...(highlight ? { highlight } : {}),
      };
    })
    .filter((sample): sample is { text: string; highlight?: string } =>
      Boolean(sample),
    );
  if (!word && !partOfSpeech && !more.length && !samples.length) {
    return undefined;
  }
  return {
    ...(word ? { word } : {}),
    ...(partOfSpeech ? { partOfSpeech } : {}),
    ...(more.length ? { more } : {}),
    ...(samples.length ? { samples } : {}),
  };
}

function cleanWordList(words?: IWordCardWord[]): IWordCardWord[] {
  return (words ?? [])
    .map((row) => {
      const next: IWordCardWord = {};
      for (const lang of WORD_CARD_LANGUAGES) {
        const entry = cleanEntry(row?.[lang.key]);
        if (entry) next[lang.key] = entry;
      }
      return Object.keys(next).length ? next : null;
    })
    .filter((row): row is IWordCardWord => row != null);
}

export function cleanWordCardWords(
  words?: IWordCardWordsByLevel | IWordCardWord[],
): IWordCardWordsByLevel {
  if (Array.isArray(words)) {
    return { A1: cleanWordList(words), B1: [], C1: [] };
  }
  return {
    A1: cleanWordList(words?.A1),
    B1: cleanWordList(words?.B1),
    C1: cleanWordList(words?.C1),
  };
}

export function wordCardWordSummary(card: Partial<IWordCard>): string {
  const words = cleanWordCardWords(card.words);
  return WORD_CARD_LEVELS.map((level) => `${level} ${words[level]?.length ?? 0}`).join(
    " · ",
  );
}

const EXTENDED_JSON_NUMBER_KEYS = [
  "$numberInt",
  "$numberLong",
  "$numberDouble",
  "$numberDecimal",
];

function unwrapExtendedJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(unwrapExtendedJson);
  if (!value || typeof value !== "object") return value;

  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length === 1) {
    const [key] = keys;
    if (key === "$oid" || key === "$date") {
      return unwrapExtendedJson(record[key]);
    }
    if (EXTENDED_JSON_NUMBER_KEYS.includes(key)) {
      return Number(record[key]);
    }
  }
  return Object.fromEntries(
    keys.map((key) => [key, unwrapExtendedJson(record[key])]),
  );
}

function stripMongoShellHelpers(raw: string): string {
  return raw
    .replace(/(?:ObjectId|ISODate)\(\s*("[^"]*")\s*\)/g, "$1")
    .replace(/Number(?:Int|Long|Decimal)\(\s*"?(-?[\d.]+)"?\s*\)/g, "$1");
}

/** Parses a word card pasted from mongo or an admin API response into form values. */
export function parseWordCardJson(raw: string): IWordCard {
  const parsed = unwrapExtendedJson(JSON.parse(stripMongoShellHelpers(raw)));

  let source: unknown = Array.isArray(parsed) ? parsed[0] : parsed;
  if (
    source &&
    typeof source === "object" &&
    !("words" in source) &&
    !("title" in source) &&
    "data" in source
  ) {
    source = (source as { data: unknown }).data;
  }
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    throw new Error("JSON must be a word card object");
  }

  const {
    _id: _ignoredId,
    __v: _ignoredVersion,
    createDate: _ignoredDate,
    isDeleted: _ignoredDeleted,
    creator: _ignoredCreator,
    level: _ignoredLevel,
    ...card
  } = source as Record<string, unknown>;

  const title = String(card.title ?? "").trim();
  const topic = String(card.topic ?? "").trim();
  const words = cleanWordCardWords(
    card.words as IWordCardWordsByLevel | IWordCardWord[] | undefined,
  );
  const hasWords = WORD_CARD_LEVELS.some((level) => words[level]?.length);
  if (!title && !hasWords) {
    throw new Error("JSON must be a word card object");
  }

  const category = Array.isArray(card.category)
    ? card.category.map((item) => String(item ?? "").trim()).filter(Boolean)
    : typeof card.category === "string" && card.category.trim()
      ? [card.category.trim()]
      : [];
  const descriptions = Array.isArray(card.aiGenerateDescriptions)
    ? card.aiGenerateDescriptions
        .map((line) => String(line ?? "").trim())
        .filter(Boolean)
    : [];

  return {
    title,
    topic,
    metadata: cleanWordCardMetadata(
      card.metadata as IWordCardMetadata | undefined,
    ),
    category,
    photo: String(card.photo ?? "").trim() || undefined,
    avatarPrompt: String(card.avatarPrompt ?? "").trim() || undefined,
    words,
    isActive: card.isActive === true,
    aiGenerateDescriptions: descriptions,
  };
}

export function cleanWordCardForSave(values: IWordCard): IWordCard {
  const category = Array.from(
    new Set((values.category ?? []).map((item) => item.trim()).filter(Boolean)),
  );
  return {
    title: values.title.trim(),
    metadata: cleanWordCardMetadata(values.metadata),
    topic: values.topic.trim(),
    category,
    photo: values.photo?.trim() || undefined,
    avatarPrompt: values.avatarPrompt?.trim() || undefined,
    words: cleanWordCardWords(values.words),
    isActive: values.isActive,
    aiGenerateDescriptions: (values.aiGenerateDescriptions ?? [])
      .map((line) => line.trim())
      .filter(Boolean),
  };
}
